import { Button, HfInput, ModalDialog, TabViewPanel, TabViewRef, Tooltip } from '@myosh/odin-components';
import { t } from 'i18next';
import { isBoolean, isEmpty, isEqual, isNil, omitBy } from 'lodash';
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useForm, Resolver } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { labelDecimalFormatter, labelFormatter } from '../../helpers/getLabelFormatter';
import { detailsWindowApi } from '../../redux/config/details-window';
import {
  Colors,
  KpiTargetConfiguration,
  RollingFR,
  useUpdateWidgetConfigurationDataMutation,
  WidgetConfiguration,
  WidgetData,
} from '../../redux/config/widgetApi';
import useInvalidateChartResponse from '../Charts/hooks/useInvalidateChartResponse';
import { WidgetConfigsGenericWrapper } from '../common/chart-generic-wrapper';
import { isTableType } from '../common/is-table-type';
import DashboardTabView from '../DashboardTabView/dashboard-tab-view';
import { useWidgetConfigs } from './hooks/useWidgetConfigs';
import AiSettingTab from './Tabs/ai-setting-tab';
import ConfigurationTabContent from './Tabs/configuration-setting-tab';
import DataColumnsSettingTab from './Tabs/data-columns-setting-tab';
import KpiTargetPercentageSettingTab from './Tabs/kpi-target-percentage-setting-tab';
import KpiTargetSettingTab, { months } from './Tabs/kpi-target-setting-tab';
import toast from 'react-hot-toast';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { yupResolver } from '@hookform/resolvers/yup';
import { isValidNumber } from '../../helpers/general-helpers';
import {
  getValidationSchema,
  computeIsDirty,
  isSaveDisabled,
  type LabelFormatType,
} from './widget-settings-validation';

interface SettingsWindowProperties {
  visible: boolean;
  onDialogHidden: () => void;
  widgetConfiguration: WidgetConfigsGenericWrapper;
  aiEnabled: boolean;
}

type TabContentProperties = {
  header: string;
  key?: string;
  children?: ReactNode | ReactElement;
};

export type KpiTargetPercentage = {
  [key: string]: string;
};

export interface DefaultValueProps {
  displayLegend?: boolean;
  labelFormat?: LabelFormatType[];
  labelPlacement?: string;
  labelDecimals?: number;
  displayDecimalPlaces?: number;
  showTop?: string;
  kpiTargetConfiguration?: Partial<KpiTargetConfiguration>;
  secondColorEnabled?: boolean;
  kpiTargetForColumnKpiPercentage?: KpiTargetPercentage;
  colors?: Colors;
  sortOrder?: Array<string>;
  form?: string[];
  field?: string;
  secondField?: string;
  module?: string;
  notBlank?: boolean;
  globalFilters?: { id?: string; value?: string; values?: string[] };
  hideBlankCategory?: boolean;

  injuryFrequencyGroupingCriteria?: LabelFormatType[];
  injuryFrequencyFields?: LabelFormatType[];
  injuryFrequencyInjuryClassificationFieldName?: LabelFormatType;
  injuryFrequencyInjuryClassificationValues?: LabelFormatType[];

  hoursWorkedGroupingCriteria?: LabelFormatType[];
  hoursWorkedFields?: LabelFormatType[];
  hoursWorkedInjuryClassificationFieldName?: LabelFormatType;
  hoursWorkedFieldName?: LabelFormatType;

  yScaleMin?: string;
  yScaleMax?: string;
}
export interface DataUpdatedProps {
  id: string | undefined;
  title?: string;
  type?: string;
  widgetConfig?: Partial<WidgetConfiguration>;
  widgetData?: Partial<WidgetData>;
  isTable?: boolean;
  injuryFrequencyAIConfig?: {
    [key: string]: object | string | string[] | boolean;
  };
  hoursWorkedAIConfig?: {
    [key: string]: object | string | string[] | boolean;
  };
  widgetSubType?: 'STACKED_COLUMN' | 'SIDE_BY_SIDE_COLUMN' | 'GROUPED_BY_DATE_COLUMN';
}

const TabContent = ({ header, key = header, children }: TabContentProperties) => {
  return children ? (
    <TabViewPanel header={header} key={key}>
      <div className="mx-0 w-auto flex-shrink px-0">{children}</div>
    </TabViewPanel>
  ) : null;
};

export default function WidgetSettingsDialog({
  visible,
  onDialogHidden,
  widgetConfiguration,
  aiEnabled,
}: SettingsWindowProperties) {
  const tabViewRef = useRef<TabViewRef>(null);

  const [colorsSelection, setColorsSelection] = useState<Record<string, string> | undefined>();
  const [defaultColor, setDefaultColor] = useState<string>();
  const [secondColor, setSecondColor] = useState<string>();
  const [lineColorSelection, setLineColorSelection] = useState<string>();
  const [sortOrder, setSortOrder] = useState<string[] | undefined>(widgetConfiguration.widgetConfig?.sortOrder);
  const [visibleColumns, setVisibleColumns] = useState<string[] | undefined>(
    widgetConfiguration.widgetData.visibleColumns
  );
  const [kpiTargetConfiguration, setKpiTargetConfiguration] = useState<KpiTargetConfiguration | undefined>();
  const [saveButtonEnabled, setSaveButtonEnabled] = useState<boolean>(true);

  // If CHART TYPE - CHART_COLUMN_KPI_PERCENTAGE
  const [kpiTargetPercentage, setKpiTargetPercentage] = useState<KpiTargetPercentage | undefined>();

  const [aiConfiguration, setAiConfiguration] = useState<undefined | RollingFR>();

  const dispatch = useDispatch();
  const [updateWidgetConfigurationData] = useUpdateWidgetConfigurationDataMutation();
  const defaultValues = useWidgetConfigs(widgetConfiguration);
  const invalidateChartData = useInvalidateChartResponse(widgetConfiguration.id);

  const showAIConfig = Boolean(aiConfiguration);

  const validationSchema = useMemo(
    () => getValidationSchema(widgetConfiguration.type, showAIConfig),
    [widgetConfiguration.type, showAIConfig]
  );

  const {
    handleSubmit,
    formState: { dirtyFields, errors, isDirty: isFormDirty },
    control,
    watch,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema) as Resolver<DefaultValueProps>,
  });
  const watchLabelFormat = watch('labelFormat');

  // isDirty tracks whether the form has unsaved changes for the Save button's disabled state.
  // We include both isFormDirty and dirtyFields in dependencies because:
  // - isFormDirty (boolean) triggers re-renders when form dirty state changes
  // - dirtyFields is used in the callback via Object.keys(dirtyFields).length, and its
  //   reference may change when react-hook-form updates the form state proxy
  const isDirty = useMemo(
    () =>
      computeIsDirty({
        dirtyFieldCount: Object.keys(dirtyFields).length,
        colorsSelection,
        lineColorSelection,
        defaultColor,
        secondColor,
        sortOrder,
        originalSortOrder: widgetConfiguration.widgetConfig?.sortOrder,
        visibleColumns,
        originalVisibleColumns: widgetConfiguration.widgetData.visibleColumns,
      }),
    [
      isFormDirty,
      dirtyFields,
      colorsSelection,
      lineColorSelection,
      defaultColor,
      secondColor,
      sortOrder,
      visibleColumns,
      widgetConfiguration.widgetConfig?.sortOrder,
      widgetConfiguration.widgetData.visibleColumns,
    ]
  );

  const getUpdateWidgetData = useCallback(
    (data: FieldValues) => {
      const finalResult: Record<string, unknown> = {
        displayLegend: data.displayLegend,
        excludeArchivedRecords: data.excludeArchivedRecords?.checked,
        ignoreDateFilters: data.ignoreDateFilters?.checked,
        globalFilters: data.globalFilters ? [data.globalFilters] : undefined,
      };
      if (!isEqual(visibleColumns, widgetConfiguration.widgetData.visibleColumns)) {
        finalResult.visibleColumns = visibleColumns;
      }
      if (widgetConfiguration && widgetConfiguration.widgetData) {
        const rollingFR = widgetConfiguration.widgetData.rollingFR;
        const rollingTRIFR = widgetConfiguration.widgetData.rollingTRIFR;

        if (rollingFR) {
          finalResult.rollingFR = {
            ...rollingFR,
            displayValues: data.displayRollingLine,
          };
        }
        if (rollingTRIFR) {
          finalResult.rollingTRIFR = {
            ...rollingTRIFR,
            displayValues: data.displayRollingLine,
          };
        }
      }

      return omitBy(finalResult, isNil);
    },
    [visibleColumns, widgetConfiguration]
  );

  const onSubmit = handleSubmit((data: FieldValues) => {
    const kpiDefaultValues = defaultValues?.kpiTargetConfiguration;

    // handleSubmit validates the form; this callback only executes if validation passes.
    // Check for changes at submission time using computeIsDirty to avoid stale closure issues.
    const hasChanges = computeIsDirty({
      dirtyFieldCount: Object.keys(dirtyFields).length,
      colorsSelection,
      lineColorSelection,
      defaultColor,
      secondColor,
      sortOrder,
      originalSortOrder: widgetConfiguration.widgetConfig?.sortOrder,
      visibleColumns,
      originalVisibleColumns: widgetConfiguration.widgetData.visibleColumns,
    });

    if (!hasChanges) {
      // No changes to submit, just close the dialog
      onDialogHidden();
      return;
    }
    const dataUpdated: DataUpdatedProps = {
      id: widgetConfiguration.id,
      isTable: isTableType(widgetConfiguration.type),
    };
    const updatedWidgetData = getUpdateWidgetData(data);
    if (!isEmpty(updatedWidgetData)) {
      dataUpdated.widgetData = updatedWidgetData;
    }

    // Reset details-window API cache when visible columns change to ensure fresh data.
    // Note: Cache is intentionally NOT reset for color or sort order changes since those
    // don't affect the underlying data fetched by details-window API.
    if (!isEqual(visibleColumns, widgetConfiguration.widgetData.visibleColumns)) {
      dispatch(detailsWindowApi.util.resetApiState());
    }

    dataUpdated.widgetConfig = omitBy(
      {
        labelFormat: data.labelFormat ? labelFormatter(data.labelFormat) : undefined,
        labelPlacement: data.labelPlacement?.value,
        labelDecimals: labelDecimalFormatter(data.labelDecimals),
        showTop: data.showTop?.value,
        threshold: data.threshold,
        hideBlankCategory: data.hideBlankCategory?.checked,
        kpiTargetConfiguration:
          'hideKPI' in data || isBoolean(data.onFRAxis)
            ? getKpiTargetsConfig(omitBy(data, isNil), kpiDefaultValues)
            : undefined,
        secondColorEnabled: isBoolean(data.secondColorEnabled)
          ? data.secondColorEnabled
          : defaultValues.secondColorEnabled,
        displayDecimalPlaces: labelDecimalFormatter(data.displayDecimalPlaces),
        colors: colorsSelection ? { ...widgetConfiguration.widgetConfig?.colors, ...colorsSelection } : undefined,
        sortOrder,
        defaultColor,
        secondColor,
        kpiTargetForColumnKpiPercentage: { ...kpiTargetPercentage, ...data.kpiTargetForColumnKpiPercentage },
      },
      isNil
    );

    if (aiConfiguration) {
      dataUpdated.injuryFrequencyAIConfig = {
        formId: aiConfiguration!.form,
        moduleId: aiConfiguration!.module,
        fields: data.injuryFrequencyFields.map((item: LabelFormatType) => item.value),
        groupingCriteria: data.injuryFrequencyGroupingCriteria.map((item: LabelFormatType) => item.value),
        injuryClassificationFieldName: data.injuryFrequencyInjuryClassificationFieldName.value,
        injuryClassificationValues: data.injuryFrequencyInjuryClassificationValues.map(
          (item: LabelFormatType) => item.value
        ),
      };
      dataUpdated.hoursWorkedAIConfig = {
        formId: aiConfiguration!.hours.form,
        moduleId: aiConfiguration!.hours.module,
        fields: data.hoursWorkedFields.map((item: LabelFormatType) => item.value),
        groupingCriteria: data.hoursWorkedGroupingCriteria.map((item: LabelFormatType) => item.value),
        injuryClassificationFieldName: data.hoursWorkedInjuryClassificationFieldName.value,
        hoursWorkedFieldName: data.hoursWorkedFieldName.value,
      };
    }

    dataUpdated.widgetConfig.yScaleMax = isValidNumber(data.yScaleMax) ? Number(data.yScaleMax) : null;
    dataUpdated.widgetConfig.yScaleMin = isValidNumber(data.yScaleMin) ? Number(data.yScaleMin) : null;

    updateWidgetConfigurationData(omitBy(dataUpdated, isEmpty))
      .then((response) => {
        if (kpiTargetConfiguration || kpiTargetPercentage || dirtyFields.hideBlankCategory) {
          invalidateChartData();
        }

        if ('error' in response) {
          toast.error((response.error as FetchBaseQueryError).data as string);
        } else {
          toast.success(String(t('widgetSubmitSuccessMsg', 'Widget updated successfully')));
        }
      })
      .catch(() => {
        toast.error(String(t('widgetSubmitErrorMsg', 'Failed to save widget config')));
      });

    // Close dialog after submission
    onDialogHidden();
  });

  // AI-related field names from aiExtensionSchema
  const aiFieldNames = [
    'injuryFrequencyGroupingCriteria',
    'injuryFrequencyFields',
    'injuryFrequencyInjuryClassificationFieldName',
    'injuryFrequencyInjuryClassificationValues',
    'hoursWorkedGroupingCriteria',
    'hoursWorkedFields',
    'hoursWorkedFieldName',
    'hoursWorkedInjuryClassificationFieldName',
  ];

  const handleClickSave = () => {
    // Only switch to AI tab if there are validation errors specifically on AI fields
    if (showAIConfig && !isEmpty(errors)) {
      const hasAiErrors = Object.keys(errors).some((key) => aiFieldNames.includes(key));
      if (hasAiErrors) {
        tabViewRef.current?.setActiveTab(4);
      }
    }
  };

  const monthsValueConfiguration = useCallback(
    (configKPI: FieldValues, defaultConfigKpi?: Partial<KpiTargetConfiguration>) => {
      const monthsValues: { [key: string]: unknown; values?: { [key: string]: unknown } } = {};
      const value: { [key: string]: unknown } = {};
      if (configKPI && defaultConfigKpi) {
        for (let i = 0; i < months.length; i++) {
          const currentMonth = months[i];
          const currMonthValue = `${[currentMonth.toLowerCase()]}Value`;

          Object.assign(monthsValues, {
            [currMonthValue]: !isNaN(Number(configKPI[currentMonth]))
              ? Number(configKPI[currentMonth])
              : defaultConfigKpi[currMonthValue],
          });
          Object.assign(value, {
            [currentMonth]: !isNaN(Number(configKPI[currentMonth]))
              ? Number(configKPI[currentMonth])
              : defaultConfigKpi[currMonthValue],
          });
        }
      }

      monthsValues.value = value;
      return monthsValues;
    },
    []
  );

  const getKpiTargetsConfig = useCallback(
    (configKPI: FieldValues, defaultConfigKpi?: Partial<KpiTargetConfiguration>) => {
      if (configKPI && defaultConfigKpi) {
        const hideKPI = 'hideKPI' in configKPI ? configKPI.hideKPI : defaultConfigKpi.hideKPI;
        return {
          hideKPI: hideKPI,
          caption: configKPI.caption ?? defaultConfigKpi.caption,
          onFRAxis: configKPI.onFRAxis ?? defaultConfigKpi.onFRAxis,
          id: widgetConfiguration.widgetConfig?.kpiTargetConfiguration?.id as string,
          lineColor: lineColorSelection ?? defaultConfigKpi.lineColor,
          ...monthsValueConfiguration(configKPI, defaultConfigKpi),
        };
      }
    },
    [lineColorSelection, monthsValueConfiguration, widgetConfiguration.widgetConfig?.kpiTargetConfiguration?.id]
  );

  // Once we receive a new value from the BE, reset local values to defaults
  useEffect(() => {
    const { widgetConfig, widgetData } = widgetConfiguration;

    setColorsSelection(undefined);

    if (widgetConfig) {
      setSortOrder(widgetConfig.sortOrder);
      setKpiTargetConfiguration(widgetConfig.kpiTargetConfiguration);

      if (!isEmpty(widgetConfig.kpiTargetForColumnKpiPercentage)) {
        setKpiTargetPercentage(widgetConfig.kpiTargetForColumnKpiPercentage);
      }
    }

    if (aiEnabled && (widgetData?.rollingFR || widgetData?.rollingTRIFR || widgetData?.rollingSR)) {
      const config = widgetData.rollingFR ?? widgetData.rollingTRIFR ?? widgetData?.rollingSR;
      if (config.form) {
        setAiConfiguration(widgetData.rollingFR ?? widgetData.rollingTRIFR ?? widgetData?.rollingSR);
      }
    }

    reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- aiEnabled, defaultValues, and reset are intentionally excluded to prevent infinite loops
  }, [widgetConfiguration]);

  const errorMessage = useMemo(() => {
    if (!saveButtonEnabled) {
      return t('columnMustBeSelected');
    }
  }, [saveButtonEnabled]);

  const footer = (
    <div className="content-en flex items-end justify-end gap-2 pt-1">
      <Tooltip tooltipZIndexCheck disabled={saveButtonEnabled} description={errorMessage}>
        <Button
          type="primary"
          variant="alternative"
          fullWidth={false}
          htmlType="submit"
          onSubmit={onSubmit}
          onClick={handleClickSave}
          form="widgetSettingForm"
          disabled={isSaveDisabled(saveButtonEnabled, isDirty)}
        >
          {String(t('save'))}
        </Button>
      </Tooltip>

      <Button type="default" variant="alternative" fullWidth={false} htmlType="button" onClick={onDialogHidden}>
        {String(t('close'))}
      </Button>
    </div>
  );

  const getDialogTitle = useMemo(() => {
    if (widgetConfiguration) {
      return `${widgetConfiguration.title} (${widgetConfiguration.type}) ${t('settings')}`;
    }
    return t('widgetConfiguration');
  }, [widgetConfiguration]);

  const handleDialogHidden = () => {
    reset();
    onDialogHidden();
    setSaveButtonEnabled(true);
  };

  return (
    <ModalDialog
      visible={visible}
      closeOnEscape={true}
      footer={footer}
      buttonsCloseModal={true}
      header={getDialogTitle}
      hidden={handleDialogHidden}
      maxDialogWidth="55vw"
      minDialogHeight="85vh"
      maxDialogHeight="95vh"
      minDialogWidth="35vw"
      transitionDuration={0}
    >
      <form id="widgetSettingForm" onSubmit={onSubmit} className="flex h-full w-full flex-col">
        <DashboardTabView ref={tabViewRef}>
          <TabContent header={t('configuration')}>
            <ConfigurationTabContent
              control={control}
              widgetConfiguration={widgetConfiguration}
              watchLabelFormat={watchLabelFormat}
              setColors={(colors) => setColorsSelection(colors)}
              setDefaultColor={(color) => setDefaultColor(color)}
              setSecondColor={(color) => setSecondColor(color)}
              setSortOrder={(sortOrder) => setSortOrder(sortOrder)}
            />
            {(widgetConfiguration.type === 'CHART_COLUMN' ||
              widgetConfiguration.type === 'CHART_KPI' ||
              widgetConfiguration.type === 'COMPARED_FR_LINE') && (
              <div className="mt-2.5 flex w-full gap-1">
                <div className="flex-1">
                  <HfInput control={control} name="yScaleMin" label={String(t('yScaleMin'))} type="number" />
                </div>
                <div className="flex-1">
                  <HfInput control={control} name="yScaleMax" label={String(t('yScaleMax'))} type="number" />
                </div>
              </div>
            )}
          </TabContent>
          {widgetConfiguration.calculatedVisibleColumns && widgetConfiguration?.widgetData?.forms?.[0] ? (
            <TabContent header={t('dataColumns')}>
              <DataColumnsSettingTab
                setVisibleColumn={(visible) => {
                  setVisibleColumns(visible);
                  setSaveButtonEnabled(visible.length > 0);
                }}
                visibleColumns={widgetConfiguration.widgetData.visibleColumns}
                pureVisibleColumns={widgetConfiguration.widgetData.pureVisibleColumns}
                module={widgetConfiguration.widgetData.module}
                form={widgetConfiguration.widgetData.forms[0]}
                control={control}
              />
            </TabContent>
          ) : (
            <></>
          )}
          {kpiTargetConfiguration ? (
            <TabContent header={t('kpiTargets')}>
              <KpiTargetSettingTab
                control={control}
                kpiTargetConfiguration={kpiTargetConfiguration}
                setLineColorSelection={(color) => setLineColorSelection(color)}
              />
            </TabContent>
          ) : (
            <></>
          )}

          {kpiTargetPercentage ? (
            <TabContent header={t('kpiTargets')}>
              <KpiTargetPercentageSettingTab control={control} kpiTargetConfiguration={kpiTargetPercentage} />
            </TabContent>
          ) : (
            <></>
          )}
          {aiConfiguration ? (
            <TabContent header={t('ai', 'AI')}>
              <AiSettingTab
                control={control}
                aiConfiguration={aiConfiguration}
                watch={watch}
                getValues={getValues}
                setValue={setValue}
                errors={errors}
              />
            </TabContent>
          ) : (
            <></>
          )}
        </DashboardTabView>
      </form>
    </ModalDialog>
  );
}
